import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, RefreshCw, CheckCircle2, FlaskConical, TicketPercent } from "lucide-react";
import { useState } from "react";
import { api } from "@/lib/api";
import { ErrorState } from "@/components/empty-state";

export const Route = createFileRoute("/admin/gameboost")({
  component: AdminGameBoost,
});

function AdminGameBoost() {
  const qc = useQueryClient();
  const [limit, setLimit] = useState<string>("");
  const status = useQuery({
    queryKey: ["admin-gameboost-status"],
    queryFn: () => api.adminGameBoostStatus(),
    refetchInterval: 4000,
  });
  const errors = useQuery({
    queryKey: ["admin-gameboost-errors"],
    queryFn: () => api.adminImportErrors(),
    refetchInterval: 8000,
  });
  const run = useMutation({
    mutationFn: (opts: { dryRun?: boolean }) =>
      api.adminImportGameBoost({
        dryRun: opts.dryRun,
        limit: limit ? Number(limit) : undefined,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-gameboost-status"] }),
  });

  const data = status.data?.data;
  const jobs = data?.jobs ?? [];
  const isRunning = jobs[0]?.status === "running" || run.isPending;

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold">GameBoost import</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            Imports the GameBoost game-keys XML feed. Each product is emitted as EUR and USD
            offers so users on either currency see a shop-native price. Affiliate tag{" "}
            <code>?ref=NEXKEY</code> is enforced on every URL.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="number"
            min={1}
            value={limit}
            onChange={(e) => setLimit(e.target.value)}
            placeholder="Limit (optional)"
            className="w-40 rounded-md border border-border bg-surface px-3 py-2 text-sm"
          />
          <button
            onClick={() => run.mutate({ dryRun: true })}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-2 text-sm font-semibold disabled:opacity-50"
          >
            <FlaskConical className="h-4 w-4" /> Dry run
          </button>
          <button
            onClick={() => run.mutate({})}
            disabled={isRunning}
            className="inline-flex items-center gap-1 rounded-md bg-primary-gradient px-4 py-2 text-sm font-semibold text-primary-foreground disabled:opacity-50"
          >
            <Play className="h-4 w-4" /> {isRunning ? "Running…" : "Start import"}
          </button>
        </div>
      </header>

      {run.isError && <ErrorState message={(run.error as Error).message} />}

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Feed</h3>
          <div className="mt-1 truncate text-xs text-muted-foreground">{data?.feedUrl ?? "—"}</div>
          <div className="mt-2 space-y-1 text-xs">
            {(data?.currencies ?? []).map((c: string) => (
              <div key={c} className="flex items-center justify-between">
                <span className="font-mono">{c}</span>
                <span className="text-muted-foreground">shop-native</span>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center gap-1 text-xs text-success">
            <CheckCircle2 className="h-3 w-3" /> affiliate{" "}
            <span className="font-mono">?ref={data?.affiliateTag ?? "NEXKEY"}</span>
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Coupon</h3>
          {data?.coupon ? (
            <>
              <div className="mt-2 inline-flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/10 px-3 py-1.5 text-sm font-bold text-primary">
                <TicketPercent className="h-4 w-4" /> {data.coupon.code}
                <span className="rounded bg-primary/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wider">
                  −{data.coupon.percentOff}%
                </span>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Applied automatically to sorting and best-price. UI always shows the
                regular shop price alongside the reduced coupon price.
              </p>
            </>
          ) : (
            <div className="mt-1 text-xs text-muted-foreground">No coupon configured</div>
          )}
        </div>
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="font-display text-lg font-semibold">Shop</h3>
          <div className="mt-1 text-sm">
            {data?.shop?.name ?? "Not created yet — will be created on first non-dry run"}
          </div>
          <div className="text-xs text-muted-foreground">{data?.offerCount ?? 0} active offers</div>
          <button
            onClick={() => status.refetch()}
            className="mt-3 inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs font-semibold"
          >
            <RefreshCw className="h-3 w-3" /> Refresh
          </button>
        </div>
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent jobs</h2>
        {status.isError && <ErrorState message={(status.error as Error).message} />}
        {jobs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No GameBoost imports have run yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Started</th>
                  <th className="px-4 py-3">Finished</th>
                  <th className="px-4 py-3 text-right">Matched</th>
                  <th className="px-4 py-3 text-right">Unmatched</th>
                  <th className="px-4 py-3 text-right">Failed</th>
                  <th className="px-4 py-3 text-right">Imported</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j: import("@/lib/api").ImportJob) => (
                  <tr key={j.id} className="border-t border-border/60">
                    <td className="px-4 py-2">{j.source}</td>
                    <td className="px-4 py-2">
                      <span className={
                        j.status === "completed" ? "text-success" :
                        j.status === "running" ? "text-primary" :
                        j.status === "failed" ? "text-destructive" : "text-muted-foreground"
                      }>{j.status}</span>
                    </td>
                    <td className="px-4 py-2 text-muted-foreground">{new Date(j.startedAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{j.finishedAt ? new Date(j.finishedAt).toLocaleString() : "—"}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.matchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{(j.unmatchedItems ?? 0).toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-destructive">{j.failedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono text-success">{j.importedItems.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{j.totalItems.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <h2 className="mb-3 font-display text-lg font-semibold">Recent unmatched / failed products</h2>
        {errors.isSuccess && errors.data.data.length === 0 && (
          <p className="text-sm text-muted-foreground">No errors logged.</p>
        )}
        {errors.isSuccess && errors.data.data.length > 0 && (
          <div className="overflow-x-auto rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-surface text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">When</th>
                  <th className="px-4 py-3">Product</th>
                  <th className="px-4 py-3">Reason</th>
                </tr>
              </thead>
              <tbody>
                {errors.data.data.slice(0, 50).map((e: { id: string; createdAt: string; rawPayload?: { title?: string; name?: string }; errorMessage?: string }) => (
                  <tr key={e.id} className="border-t border-border/60">
                    <td className="px-4 py-2 text-muted-foreground">{new Date(e.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2 text-muted-foreground">{e.rawPayload?.title ?? e.rawPayload?.name ?? "—"}</td>
                    <td className="px-4 py-2 text-destructive">{e.errorMessage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
